// @owners { team: patients }
import { updateOrder } from '~shared/actions/checkout';
import {
  type CardData,
  type CreatePaymentMethodRequest,
  type PlaidData,
  createPaymentMethodSucceeded,
  deletePaymentMethodSucceeded,
  generateStripeData,
} from '~shared/actions/paymentMethods';
import { PAYMENT_METHOD_TYPES, type PaymentMethodType } from '~shared/constants';
import { destroy, post } from '~shared/helpers/apiHelper';
import { paymentMethods } from '~shared/queries/query-keys/paymentMethods';
import { useIsMutating, useMutation, useQueryClient } from '~shared/react-query';
import { useDispatchShared } from '~shared/store';
import { type PaymentMethod } from '~shared/types';
import { type APIError } from '~shared/types/APIError';

export { type CardData, type CreatePaymentMethodRequest, type PlaidData } from '~shared/actions/paymentMethods';

export type SetupIntentData = {
  setupIntentId: string;
  paymentMethodType?: PaymentMethodType;
};

// All create-flavored mutations (card, setup intent, bank account) share this key so
// useIsCreatingPaymentMethod observes any in-flight create regardless of which hook fired it.
const CREATE_PAYMENT_METHOD_MUTATION_KEY = ['createPaymentMethod'];
const DELETE_PAYMENT_METHOD_MUTATION_KEY = ['deletePaymentMethod'];

const unwrapResponse = (response: PaymentMethod & { error?: APIError }): PaymentMethod => {
  if (response.error) {
    throw response.error;
  }
  return response;
};

const postPaymentMethod = async (request: CreatePaymentMethodRequest): Promise<PaymentMethod> =>
  unwrapResponse(await post('/payment_methods', request));

/**
 * Shared onSuccess for the create-payment-method mutations. Mirrors the deprecated
 * createPaymentMethod thunk's side effects:
 * - keeps the Redux paymentMethods slice in sync (read by the getOrder / shipment
 *   payment Reselect selectors) without waiting for the invalidated query to refetch
 * - sets the new payment method on the draft order per the checkout context it was
 *   added from (primary / secondary / tips / Apple Pay)
 */
const useOnPaymentMethodCreated = () => {
  const dispatch = useDispatchShared();
  const queryClient = useQueryClient();

  return (paymentMethod: PaymentMethod, paymentMethodType?: PaymentMethodType) => {
    dispatch(createPaymentMethodSucceeded(paymentMethod));

    if (paymentMethodType === PAYMENT_METHOD_TYPES.APPLE_PAY_W_TIP) {
      dispatch(
        updateOrder({
          payment_method_id: paymentMethod.id,
          tip_payment_method_id: paymentMethod.id,
        }),
      );
    }

    if (paymentMethodType === PAYMENT_METHOD_TYPES.PRIMARY || paymentMethodType === PAYMENT_METHOD_TYPES.APPLE_PAY) {
      dispatch(
        updateOrder({
          payment_method_id: paymentMethod.id,
        }),
      );
    }

    if (paymentMethodType === PAYMENT_METHOD_TYPES.SECONDARY) {
      dispatch(
        updateOrder({
          second_payment_method_id: paymentMethod.id,
        }),
      );
    }

    if (paymentMethodType === PAYMENT_METHOD_TYPES.PRE_TIP || paymentMethodType === PAYMENT_METHOD_TYPES.POST_TIP) {
      dispatch(
        updateOrder({
          tip_payment_method_id: paymentMethod.id,
        }),
      );
    }

    void queryClient.invalidateQueries({ queryKey: paymentMethods.fetchAll._def });
  };
};

/**
 * Create a credit/debit card payment method from a Stripe card token (legacy card form,
 * Apple Pay) — React Query replacement for the deprecated createCreditCardPaymentMethod
 * thunk. Rejects with the APIError on failure.
 */
export const useCreateCreditCardPaymentMethod = () => {
  const onCreated = useOnPaymentMethodCreated();

  return useMutation<PaymentMethod, APIError, CardData>({
    mutationKey: CREATE_PAYMENT_METHOD_MUTATION_KEY,
    mutationFn: ({ token }) => postPaymentMethod({ type: 'credit', stripe_data: generateStripeData(token) }),
    onSuccess: (paymentMethod, { paymentMethodType }) => {
      onCreated(paymentMethod, paymentMethodType);
    },
  });
};

/**
 * Create a credit/debit card payment method from a confirmed Stripe SetupIntent
 * (Payment Element / PaymentSheet add-card flows) — React Query replacement for the
 * deprecated createCreditCardPaymentMethodFromSetupIntent thunk.
 */
export const useCreateCreditCardPaymentMethodFromSetupIntent = () => {
  const onCreated = useOnPaymentMethodCreated();

  return useMutation<PaymentMethod, APIError, SetupIntentData>({
    mutationKey: CREATE_PAYMENT_METHOD_MUTATION_KEY,
    mutationFn: ({ setupIntentId }) =>
      postPaymentMethod({ type: 'credit', stripe_data: { setup_intent_id: setupIntentId } }),
    onSuccess: (paymentMethod, { paymentMethodType }) => {
      onCreated(paymentMethod, paymentMethodType);
    },
  });
};

/**
 * Create an ACH payment method from a Plaid Link success — React Query replacement for
 * the deprecated createBankAccountPaymentMethod thunk.
 */
export const useCreateBankAccountPaymentMethod = () => {
  const onCreated = useOnPaymentMethodCreated();

  return useMutation<PaymentMethod, APIError, PlaidData>({
    mutationKey: CREATE_PAYMENT_METHOD_MUTATION_KEY,
    mutationFn: ({ publicToken, accountId, institutionName }) =>
      postPaymentMethod({
        type: 'ach',
        plaid_data: {
          public_token: publicToken,
          account_id: accountId,
          institution_name: institutionName,
        },
      }),
    onSuccess: (paymentMethod, { paymentMethodType }) => {
      onCreated(paymentMethod, paymentMethodType);
    },
  });
};

/**
 * True while any create-payment-method mutation (card, setup intent, or bank account)
 * is in flight, from any component — replaces cross-component reads of
 * ui.loading.createPaymentMethodLoading (e.g. the checkout payment method selector
 * showing a spinner while Apple Pay setup runs elsewhere).
 */
export const useIsCreatingPaymentMethod = () => {
  return useIsMutating({ mutationKey: CREATE_PAYMENT_METHOD_MUTATION_KEY }) > 0;
};

/**
 * Delete a payment method — React Query replacement for the deprecated
 * deletePaymentMethod thunk. Rejects with the APIError on failure. The Redux slice is
 * updated immediately (same as the thunk) so checkout selectors don't briefly see the
 * deleted method while the invalidated query refetches.
 */
export const useDeletePaymentMethod = () => {
  const dispatch = useDispatchShared();
  const queryClient = useQueryClient();

  return useMutation<PaymentMethod, APIError, PaymentMethod>({
    mutationKey: DELETE_PAYMENT_METHOD_MUTATION_KEY,
    mutationFn: async (paymentMethod) => unwrapResponse(await destroy(`/payment_methods/${paymentMethod.id}`)),
    onSuccess: (_deleted, paymentMethod) => {
      dispatch(deletePaymentMethodSucceeded(paymentMethod));
      void queryClient.invalidateQueries({ queryKey: paymentMethods.fetchAll._def });
    },
  });
};
